/**
 * Dependency-free Code 128 (subset B) barcode → SVG. Readable by phone
 * cameras and standard USB/Bluetooth scanners (ID-003). The payload is the
 * CivilHound paper number only (ID-002).
 */

// Bar/space module widths for symbol values 0–106 (106 = STOP, 7 elements).
export const CODE128_PATTERNS: readonly string[] = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];

const START_B = 104;
const STOP = 106;

/** Symbol values (start, data, checksum, stop) for a Code 128-B string. */
export function code128Values(text: string): number[] {
  const values: number[] = [START_B];
  for (const ch of text) {
    const code = ch.charCodeAt(0);
    if (code < 32 || code > 126) throw new Error(`Character "${ch}" not encodable in Code 128-B`);
    values.push(code - 32);
  }
  let sum = START_B;
  for (let i = 1; i < values.length; i++) sum += values[i]! * i;
  values.push(sum % 103);
  values.push(STOP);
  return values;
}

/** Module widths sequence alternating bar, space, bar… */
export function code128Modules(text: string): number[] {
  return code128Values(text).flatMap((v) => CODE128_PATTERNS[v]!.split('').map(Number));
}

export interface BarcodeSvgOptions {
  moduleWidth?: number;
  height?: number;
  quietZoneModules?: number;
  showText?: boolean;
}

export function code128Svg(text: string, opts: BarcodeSvgOptions = {}): string {
  const mw = opts.moduleWidth ?? 2;
  const h = opts.height ?? 60;
  const qz = (opts.quietZoneModules ?? 10) * mw;
  const textH = opts.showText === false ? 0 : 18;
  const modules = code128Modules(text);
  let x = qz;
  const rects: string[] = [];
  modules.forEach((w, i) => {
    if (i % 2 === 0) rects.push(`<rect x="${x}" y="0" width="${w * mw}" height="${h}"/>`);
    x += w * mw;
  });
  const width = x + qz;
  const label =
    textH > 0
      ? `<text x="${width / 2}" y="${h + 14}" text-anchor="middle" font-family="monospace" font-size="13">${escapeXml(text)}</text>`
      : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${h + textH}" viewBox="0 0 ${width} ${h + textH}" role="img" aria-label="Barcode ${escapeXml(text)}"><rect width="100%" height="100%" fill="#fff"/><g fill="#000">${rects.join('')}</g>${label}</svg>`;
}

function escapeXml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' })[c]!);
}
